import { NodeType, toDhtAddress, toDhtAddressRaw } from '@streamr/dht';
import { LengthPrefixedFrameDecoder, Logger, TheGraphClient, composeAbortSignals, merge, randomString, toEthereumAddress, toStreamID } from '@streamr/utils';
import { LRUCache } from 'lru-cache';
import { NetworkNodeType } from '../ConfigTypes';
import { WebStreamToNodeStream } from './WebStreamToNodeStream';
import { SEPARATOR } from './uuid';
const logger = new Logger('sdk-utils');
export const CounterId = (rootPrefix, { maxPrefixes = 256 } = {}) => {
    let counts = {}; // possible we could switch this to WeakMap and pass functions or classes.
    let didWarn = false;
    const counterIdFn = (prefix = 'ID', separator = SEPARATOR) => {
        // pedantic: wrap around if count grows too large
        counts[prefix] = (counts[prefix] + 1 || 0) % Number.MAX_SAFE_INTEGER;
        // warn once if too many prefixes
        if (!didWarn) {
            const numTracked = Object.keys(counts).length;
            if (numTracked > maxPrefixes) {
                didWarn = true;
                console.warn(`counterId should not be used for a large number of unique prefixes: ${numTracked} > ${maxPrefixes}`);
            }
        }
        // connect prefix with separator
        return [rootPrefix, prefix, counts[prefix]]
            .filter((v) => v != null) // remove {root}Prefix if not set
            .join(separator);
    };
    /**
     * Clears counts for prefix or all if no prefix supplied.
     *
     * @param {string?} prefix
     */
    counterIdFn.clear = (...args) => {
        // check length to differentiate between clear(undefined) & clear()
        if (args.length) {
            const [prefix] = args;
            delete counts[prefix];
        }
        else {
            // clear all
            counts = {};
        }
    };
    return counterIdFn;
};
export const counterId = CounterId();
export function instanceId(instance, suffix = '') {
    return counterId(instance.constructor.name) + suffix;
}
export const getEndpointUrl = (baseUrl, ...pathParts) => {
    return baseUrl + '/' + pathParts.map((part) => encodeURIComponent(part)).join('/');
};
export function formStorageNodeAssignmentStreamId(clusterAddress) {
    return toStreamID('/assignments', toEthereumAddress(clusterAddress));
}
export class MaxSizedSet {
    delegate;
    constructor(maxSize) {
        this.delegate = new LRUCache({ maxSize, sizeCalculation: () => 1 });
    }
    add(value) {
        this.delegate.set(value, true);
    }
    has(value) {
        return this.delegate.has(value);
    }
    delete(value) {
        this.delegate.delete(value);
    }
}
// TODO: rename to convertNetworkPeerDescriptorToPeerDescriptor
// This function contains temporary compatibility layer which allows that PeerDescriptor can be configured with 
// "id" field instead of "nodeId" field. This is done so that pretestnet users don't need to change their configs.
// After strear-1.0 testnet1 or mainnet starts, remove this hack.
// - Good to ensure at that point that the new format has landed to the public documentation: 
//   https://docs.streamr.network/guides/become-an-operator
// - or maybe NET-1133 or NET-1004 have been implemented and the documentation no longer mentions the low
//   level way of configuring the entry points.
// Actions:
// - remove "temporary compatibility" test case from Broker's config.test.ts 
// - remove "id" property from config.schema.json (line 536) and make "nodeId" property required
// - remove "id" property handling from this method
export function peerDescriptorTranslator(json) {
    const type = json.type === NetworkNodeType.BROWSER ? NodeType.BROWSER : NodeType.NODEJS;
    const peerDescriptor = {
        ...json,
        nodeId: toDhtAddressRaw((json.nodeId ?? json.id)),
        type,
        websocket: json.websocket
    };
    if (peerDescriptor.id !== undefined) {
        delete peerDescriptor.id;
    }
    return peerDescriptor;
}
export function convertPeerDescriptorToNetworkPeerDescriptor(descriptor) {
    // TODO maybe we should copy most/all fields of PeerDescription (NET-1255)
    return {
        nodeId: toDhtAddress(descriptor.nodeId),
        type: descriptor.type === NodeType.NODEJS ? NetworkNodeType.NODEJS : NetworkNodeType.BROWSER,
        websocket: descriptor.websocket,
        region: descriptor.region
    };
}
export function generateClientId() {
    return counterId(typeof process !== 'undefined' && process.pid ? `${process.pid}` : randomString(4), '/');
}
// A unique internal identifier to some list of primitive values. Useful
// e.g. as a map key or a cache key.
export const formLookupKey = (key) => {
    return Array.isArray(key)
        ? key.map((a) => a.toString()).join('|')
        : key.toString();
};
/** @internal */
export const createTheGraphClient = (eventEmitter, config) => {
    const instance = new TheGraphClient({
        serverUrl: config.contracts.theGraphUrl,
        fetch: (url, init) => {
            // eslint-disable-next-line no-underscore-dangle
            const timeout = config._timeouts.theGraph.fetchTimeout;
            const signals = [AbortSignal.timeout(timeout)];
            if (init?.signal instanceof AbortSignal) {
                signals.push(init.signal);
            }
            const signal = composeAbortSignals(...signals);
            try {
                return fetch(url, merge(init, { signal }));
            }
            finally {
                signal.destroy();
            }
        },
        // eslint-disable-next-line no-underscore-dangle
        indexTimeout: config._timeouts.theGraph.indexTimeout,
        // eslint-disable-next-line no-underscore-dangle
        indexPollInterval: config._timeouts.theGraph.indexPollInterval
    });
    eventEmitter.on('contractTransactionConfirmed', (payload) => {
        if (payload.receipt != null) {
            instance.updateRequiredBlockNumber(payload.receipt.blockNumber);
        }
    });
    return instance;
};
export const createQueryString = (query) => {
    const withoutEmpty = Object.fromEntries(Object.entries(query).filter(([_k, v]) => v != null));
    return new URLSearchParams(withoutEmpty).toString();
};
export class FetchHttpStreamResponseError extends Error {
    response;
    constructor(response) {
        super(`Fetch error, url=${response.url}`);
        this.response = response;
    }
}
export const fetchLengthPrefixedFrameHttpBinaryStream = async function* (url, abortSignal) {
    logger.debug('Send HTTP request', { url });
    const abortController = new AbortController();
    const fetchAbortSignal = composeAbortSignals(abortController.signal, abortSignal);
    try {
        const response = await fetch(url, {
            signal: fetchAbortSignal
        });
        logger.debug('Received HTTP response', {
            url,
            status: response.status,
        });
        if (!response.ok) {
            throw new FetchHttpStreamResponseError(response);
        }
        if (!response.body) {
            throw new Error('No Response Body');
        }
        let stream;
        try {
            // in the browser, response.body will be a web stream. Convert this into a node stream.
            const source = WebStreamToNodeStream(response.body);
            stream = source.pipe(new LengthPrefixedFrameDecoder());
            source.on('error', (err) => stream.destroy(err));
            stream.once('close', () => {
                abortController.abort();
            });
            yield* stream;
        }
        catch (err) {
            abortController.abort();
            throw err;
        }
        finally {
            stream?.destroy();
        }
    }
    finally {
        fetchAbortSignal.destroy();
    }
};
